import { useNavigate } from 'react-router-dom';
import { useUsageStatistics } from '@/hooks/api/useStatistics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Building2,
  Users,
  FolderKanban,
  MessageSquare,
  Activity,
  Clock,
  ArrowRight,
  CheckCircle2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import Routes from '@/router/routes';

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

function formatStatus(status: string): { label: string; variant: 'default' | 'success' | 'active' | 'pending' | 'deleted' | 'info' } {
  switch (status?.toLowerCase()) {
    case 'published':
      return { label: 'Published', variant: 'active' };
    case 'completed':
      return { label: 'Completed', variant: 'success' };
    case 'draft':
      return { label: 'Draft', variant: 'default' };
    case 'ready':
      return { label: 'Ready', variant: 'info' };
    case 'waiting_approval':
      return { label: 'Awaiting Approval', variant: 'pending' };
    case 'request_approved':
      return { label: 'Approved', variant: 'info' };
    default:
      return { label: status ?? 'Unknown', variant: 'default' };
  }
}

function OverviewCardSkeleton() {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-4 rounded" />
        </div>
        <Skeleton className="h-8 w-16 mb-1" />
        <Skeleton className="h-3 w-24 mt-1.5" />
        <Skeleton className="h-3 w-20 mt-3" />
      </CardContent>
    </Card>
  );
}

function ConversationCardSkeleton() {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 mb-1">
          <Skeleton className="h-4 w-4 rounded-full" />
          <Skeleton className="h-4 w-20" />
        </div>
        <Skeleton className="h-6 w-12 mt-1" />
      </CardContent>
    </Card>
  );
}

function ListCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-32" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center justify-between py-1.5">
              <div className="flex-1 mr-3">
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-20" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-10 rounded-full" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Platform overview</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <OverviewCardSkeleton key={i} />
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <ConversationCardSkeleton key={i} />
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <ListCardSkeleton />
        <ListCardSkeleton />
        <ListCardSkeleton />
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { data: stats, isLoading, isFetching } = useUsageStatistics();

  if (isLoading && !stats) {
    return <DashboardSkeleton />;
  }

  if (!stats) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
          <p className="text-sm text-red-600 mt-1">Failed to load statistics</p>
        </div>
      </div>
    );
  }

  const overview = stats.overview ?? {};
  const conversations = stats.conversations ?? {};
  const recentActivity = stats.recentActivity ?? {};
  const latestOrganizations = stats.latestOrganizations ?? [];
  const latestProjects = stats.latestProjects ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Platform overview</p>
        </div>
        {isFetching && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Updating...
          </div>
        )}
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card
          className="cursor-pointer transition-all hover:shadow-md hover:border-primary/20"
          onClick={() => navigate(Routes.super_admin_dashboard_organizations.path)}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <div className="text-2xl font-semibold">{(overview.totalOrganizations ?? 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-0.5">Organizations</p>
            <div className="flex gap-2 mt-2">
              <span className="text-xs text-green-600 font-medium">{overview.paidOrganizations ?? 0} paid</span>
              <span className="text-xs text-muted-foreground">{overview.freeOrganizations ?? 0} free</span>
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer transition-all hover:shadow-md hover:border-primary/20"
          onClick={() => navigate(Routes.super_admin_dashboard_users.path)}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <Users className="h-5 w-5 text-muted-foreground" />
              <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <div className="text-2xl font-semibold">{(overview.totalUsers ?? 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-0.5">Users</p>
            <div className="mt-2">
              <span className="text-xs text-muted-foreground">{(overview.totalWorkspaces ?? 0).toLocaleString()} workspaces</span>
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer transition-all hover:shadow-md hover:border-primary/20"
          onClick={() => navigate(Routes.super_admin_dashboard_projects.path)}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <FolderKanban className="h-5 w-5 text-muted-foreground" />
              <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <div className="text-2xl font-semibold">{(overview.totalProjects ?? 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-0.5">Projects</p>
            <div className="mt-2">
              <span className="text-xs text-green-600 font-medium">{overview.activeProjects ?? 0} published</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-semibold">{(conversations.total ?? 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-0.5">Conversations</p>
            <div className="mt-2">
              <span className="text-xs text-muted-foreground">~{conversations.avgPerProject ?? 0} per project</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversation Breakdown */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm text-muted-foreground">Completed</span>
            </div>
            <div className="text-xl font-semibold">{(conversations.completed ?? 0).toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-muted-foreground">In Progress</span>
            </div>
            <div className="text-xl font-semibold">{(conversations.inProgress ?? 0).toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-muted-foreground">Abandoned</span>
            </div>
            <div className="text-xl font-semibold">{(conversations.abandoned ?? 0).toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Completion Rate</span>
            </div>
            <div className="text-xl font-semibold">{conversations.completionRate ?? 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section: Activity + Latest Orgs + Latest Projects */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Last 7 Days</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { label: 'New organizations', value: recentActivity.newOrganizationsLast7Days ?? 0 },
                { label: 'New projects', value: recentActivity.newProjectsLast7Days ?? 0 },
                { label: 'New conversations', value: recentActivity.newConversationsLast7Days ?? 0 },
                { label: 'Completed', value: recentActivity.completedConversationsLast7Days ?? 0, color: 'text-green-600' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{item.label}</span>
                  <span className={`text-sm font-medium tabular-nums ${item.color ?? ''}`}>
                    {item.value.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Latest Organizations */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">Latest Organizations</CardTitle>
              </div>
              <button
                onClick={() => navigate(Routes.super_admin_dashboard_organizations.path)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                View all <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2.5">
              {latestOrganizations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No organizations yet</p>
              ) : (
                latestOrganizations.map((org) => (
                  <div
                    key={org._id}
                    className="flex items-center justify-between py-1.5 cursor-pointer hover:bg-muted/50 -mx-2 px-2 rounded-md transition-colors"
                    onClick={() => navigate(`${Routes.super_admin_dashboard_organizations.path}/${org._id}`)}
                  >
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="text-sm font-medium truncate">{org.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{org.memberCount} members</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant={org.subscribed ? 'paid' : 'free'} className="text-[10px] px-1.5 py-0">
                        {org.subscribed ? 'Paid' : 'Free'}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground tabular-nums w-12 text-right">
                        {formatRelativeTime(org.createdAt)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Latest Projects */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderKanban className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">Latest Projects</CardTitle>
              </div>
              <button
                onClick={() => navigate(Routes.super_admin_dashboard_projects.path)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                View all <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2.5">
              {latestProjects.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No projects yet</p>
              ) : (
                latestProjects.map((project) => {
                  const statusInfo = formatStatus(project.status);
                  return (
                    <div
                      key={project._id}
                      className="flex items-center justify-between py-1.5 cursor-pointer hover:bg-muted/50 -mx-2 px-2 rounded-md transition-colors"
                      onClick={() => navigate(`${Routes.super_admin_dashboard_projects.path}/${project._id}`)}
                    >
                      <div className="min-w-0 flex-1 mr-3">
                        <p className="text-sm font-medium truncate">{project.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{project.organizationName}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant={statusInfo.variant} className="text-[10px] px-1.5 py-0">
                          {statusInfo.label}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground tabular-nums w-12 text-right">
                          {formatRelativeTime(project.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
